import { FORM_ERROR, SubmissionErrors } from 'final-form';
import { identity } from 'lodash';
import { FC, useState, ReactNode, FormEvent } from 'react';
import { Field, Form } from 'react-final-form';
import { Transition } from 'react-transition-group';
import { Row, Col, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { connect, MapDispatchToPropsFunction } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import CustomFormInput from '~src/shared/form/inputs/FormInput';
import { FieldType } from '~src/types';
import ProgressBar from '~src/shared/ProgressBar';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import SvgIcon from '~src/shared/SvgIcon';
import { ShowNotification } from '~src/store/app/interfaces';
import { showNotification } from '~src/store/app/actions';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import { routes } from '~src/routingConfig';
import { translateError } from '~src/services/translationServices';
import {
  ConfigDataI,
  ConfigSubmitObj,
  updateIntegrationConfig,
  activateIntegration,
} from '~src/services/fetch/import.service';
import { CompanySettingsLocationState } from '~src/modules/companySettings/types';
import { IntegrationTypes } from '~src/modules/companySettings/importUsers/types';

import IntegrationFileUpload from './IntegrationFileUpload';
import {
  ImportRow,
  ImportRightCol,
  SettingsBlock,
  RowContent,
  FormContentBox,
  ButtonsBox,
  CancelBtn,
  RowTitle,
  TitleTxt,
  BtnCell,
  ImportBtnBox,
  ProgressWrapper,
  ErrorBox,
  ButtonsRow,
  TooltipBox,
  IntegrationHelp,
} from './integrationItem.sc';
import { IntegrationItemProps, UploadingStatusI, IntegrationStatus } from './integrationItem.types';
import IntegrationStartImport from './IntegrationStartImport';
import { IntegrationItemViewI } from '~src/modules/companySettings/importUsers/IntegrationsList';
import CheckBox, { CheckBoxType } from '~src/shared/form/Checkbox';

const formAnimationTimeout = 500;

interface DispatchProps {
  showNotification: ShowNotification;
}

interface OwnProps
  extends IntegrationItemProps,
    Pick<
      RouteComponentProps<Record<string, string>, Record<string, string>, CompanySettingsLocationState>,
      'history' | 'location'
    > {
  integration: Omit<IntegrationItemViewI, 'props'>;
  uploadingStatus?: UploadingStatusI;
  getImportsStatus(id: string, integrationId?: string): void;
  updateIntegrationState(integrationId: string, config: ConfigSubmitObj): void;
}

type Props = OwnProps & DispatchProps;

const IntegrationItem: FC<Props> = (props) => {
  const [t] = useTranslation();
  const [isConfigVisible, setIsConfigVisible] = useState(false);
  const [integrationId, setIntegrationId] = useState<string>(props.integration.id!);

  const toggleConfig = () => {
    setIsConfigVisible(prevIsConfigVisible => !prevIsConfigVisible);
  };

  const submitConfig = async (values: {
    config: ConfigDataI;
    auto_import: boolean;
  }): Promise<SubmissionErrors> => {
    const notificationKey = 'x.company.import.integration_config_updated';
    const configSubmitObj: ConfigSubmitObj = {
      config: values.config,
      auto_import: values.auto_import ?? false,
    };

    try {
      if (integrationId) {
        await updateIntegrationConfig(integrationId, configSubmitObj);
      } else {
        configSubmitObj.name = props.integration.name;
        const resp = await activateIntegration(configSubmitObj);
        setIntegrationId(resp && resp.data.id);
      }
      toggleConfig();
      props.showNotification({ id: notificationKey, txtKey: notificationKey });
      props.updateIntegrationState(integrationId, configSubmitObj);
    } catch (err) {
      return { [FORM_ERROR]: translateError(t, (err as any).response.data[0].translation_key) };
    }
  };

  const renderInput = ({
    autoFocus,
    input,
    label,
    meta: { touched, error },
    placeholder,
    labelKey,
  }: FieldType<string>): ReactNode => (
    <CustomFormInput
      {...input}
      autoFocus={autoFocus}
      error={touched ? (typeof error === 'string' ? error : '') : ''}
      label={label}
      type={input.type || 'text'}
      placeholder={placeholder}
      labelKey={labelKey}
    />
  );

  const renderAutoImportCheckbox = ({ input, label }: FieldType<boolean>): ReactNode => (
    <CheckBox
      {...input}
      isChecked={input.value}
      type={CheckBoxType.CheckBox}
      label={label}
      id={props.integration.name}
    />
  );

  const startImportStatusCheck = (importId: string) => {
    const historyState = { ...props.location.state, importCanceled: false };
    props.history.replace({ state: historyState });
    props.getImportsStatus(importId, props.integration.id);
  };

  const goToImport = () => {
    props.history.push(`${routes.company.importUsers}/${props.integration.importId}`);
  };

  const uploadingStatus = props.uploadingStatus || { status: IntegrationStatus.Successful };
  const isUploading = uploadingStatus.status === IntegrationStatus.Running;
  const title = t(props.title);
  const canAutoImport =
    props.integration.name === IntegrationTypes.HrLab || props.integration.name === IntegrationTypes.Personio;

  return (
    <ImportRow>
      <RowContent>
        <RowTitle>
          {props.svgIcon && <SvgIcon name={props.svgIcon} width={40} viewBoxWidth={1000} />}
          {props.icon && <img alt={title} src={props.icon} height={40} />}
          <TitleTxt data-translation={props.title} $noIcon={!props.icon}>
            <p>{title}</p>
            {props.description && (
              <p
                dangerouslySetInnerHTML={{
                  __html:
                    typeof props.description === 'string'
                      ? t(props.description)
                      : t(props.description[0], props.description[1]),
                }}
              />
            )}
          </TitleTxt>
        </RowTitle>
        <ImportRightCol>
          {isUploading && (
            <ProgressWrapper>
              <ProgressBar
                t={t}
                width="100%"
                height={5}
                progress={uploadingStatus.progress}
                progressTxt="x.company.import.uploading_import"
              />
            </ProgressWrapper>
          )}
          <ImportBtnBox $isHidden={isUploading}>
            {integrationId &&
              (uploadingStatus.status === IntegrationStatus.PreviewReady ? (
                <PrimaryButton data-translation="x.company.import.show_preview_button" onClick={goToImport}>
                  {t('x.company.import.show_preview_button')}
                </PrimaryButton>
              ) : (
                <>
                  {props.integration.name === IntegrationTypes.Csv ? (
                    <IntegrationFileUpload
                      integrationId={integrationId}
                      uploadError={uploadingStatus.error}
                      startImportStatusCheck={startImportStatusCheck}
                    />
                  ) : (
                    <IntegrationStartImport
                      integrationId={integrationId}
                      uploadError={uploadingStatus.error}
                      startImportStatusCheck={startImportStatusCheck}
                    />
                  )}
                </>
              ))}
            <BtnCell>
              <SecondaryButton
                data-translation="x.company.import.from_csv.configure_import_button"
                onClick={toggleConfig}
              >
                {t('x.company.import.from_csv.configure_import_button')}
              </SecondaryButton>
            </BtnCell>
          </ImportBtnBox>
        </ImportRightCol>
      </RowContent>
      <Transition in={isConfigVisible} timeout={formAnimationTimeout} unmountOnExit>
        {state => (
          <SettingsBlock className={state} $timeout={formAnimationTimeout}>
            <FormContentBox>
              <Form
                initialValues={{
                  config: props.integration.config ?? props.configForm.defaultValues,
                  auto_import: props.integration.auto_import,
                }}
                onSubmit={submitConfig}
              >
                {({ handleSubmit, pristine, error, form, submitting, submitError }) => {
                  const handleSubmitAndReset = (event: FormEvent<HTMLFormElement>) => {
                    const submitResult = handleSubmit(event);
                    if (submitResult && isConfigVisible) {
                      void submitResult.then((res) => {
                        if (!res) {
                          setTimeout(form.reset, 500);
                        }
                      });
                    }
                    return submitResult;
                  };

                  return (
                    <form onSubmit={handleSubmitAndReset} noValidate>
                      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                        <Row gutter={15}>
                          {props.configForm.fields.map(field => (
                            <Col span={8} key={field.name}>
                              <Field<string, HTMLInputElement>
                                autoFocus={field.name === 'delimiter'}
                                name={`config.${field.name}`}
                                children={renderInput}
                                label={t(field.labelKey)}
                                labelKey={field.labelKey}
                                parse={
                                  identity
                                } /* `identity` function to prevent empty values from getting dropped completely on submit. see also https://github.com/final-form/final-form/blob/6fe8c19/docs/faq.md#why-does-final-form-set-my--field-value-to-undefined */
                              />
                              {field.tooltipTxt && (
                                <TooltipBox data-translation={field.tooltipTxt}>
                                  {t(field.tooltipTxt)}
                                </TooltipBox>
                              )}
                            </Col>
                          ))}
                        </Row>
                        <Row>
                          <Col span={8}>
                            {canAutoImport && (
                              <Field
                                name="auto_import"
                                label={t('x.company.import.auto_import')}
                                children={renderAutoImportCheckbox}
                              />
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <IntegrationHelp dangerouslySetInnerHTML={{ __html: t(props.integrationHelp) }} />
                        </Row>
                        {(!integrationId || !pristine) && (
                          <ButtonsBox>
                            <ButtonsRow>
                              <PrimaryButtonLoading
                                data-translation={`x.company.import.config.${
                                  integrationId ? 'save_btn' : 'activate_btn'
                                }`}
                                isLoading={submitting}
                              >
                                {t(
                                  `x.company.import.config.${
                                    !integrationId && props.integration.name === IntegrationTypes.Csv
                                      ? 'activate_btn'
                                      : 'save_btn'
                                  }`,
                                )}
                              </PrimaryButtonLoading>
                              <CancelBtn
                                data-translation="x.company.import.cancel_config"
                                type="button"
                                onClick={toggleConfig}
                              >
                                {t('x.company.import.cancel_config')}
                              </CancelBtn>
                            </ButtonsRow>
                            {submitError && <ErrorBox>{t(submitError)}</ErrorBox>}
                            {error && <ErrorBox>{t(error)}</ErrorBox>}
                          </ButtonsBox>
                        )}
                      </Space>
                    </form>
                  );
                }}
              </Form>
            </FormContentBox>
          </SettingsBlock>
        )}
      </Transition>
    </ImportRow>
  );
};

const mapDispatch2Props: MapDispatchToPropsFunction<DispatchProps, OwnProps> = dispatch => ({
  showNotification: notification => dispatch(showNotification(notification)),
});

const IntegrationItemC = connect(undefined, mapDispatch2Props)(IntegrationItem);

export default IntegrationItemC;
